import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, Plus, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  owner: "Sahip", admin: "Yönetici", admissions: "Kayıt Sorumlusu",
  guidance: "Rehber", content: "İçerik Editörü", finance: "Finans", exam_officer: "Sınav Sorumlusu",
};

export default function SchoolTeam() {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "admissions", name: "" });

  const load = () => api.get(`${V1}/school/team`).then((r) => setMembers(r.data || []));
  useEffect(() => { load(); }, []);

  const add = async () => {
    try {
      await api.post(`${V1}/school/team`, form);
      toast.success("Üye eklendi");
      setOpen(false); setForm({ email: "", role: "admissions", name: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Üye silinsin mi?")) return;
    try {
      await api.delete(`${V1}/school/team/${id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const changeRole = async (id, role) => {
    try {
      await api.patch(`${V1}/school/team/${id}/role`, { role });
      toast.success("Rol güncellendi"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  return (
    <div className="space-y-6" data-testid="school-team-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Ekip Yönetimi</div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Users className="h-7 w-7 text-primary" /> Okul Ekibi</h1>
          <p className="text-muted-foreground text-sm mt-1">Ekip üyelerinizi rolleriyle birlikte yönetin.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-member-btn"><Plus className="h-4 w-4" /> Üye Ekle</Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="text-left p-3">Kullanıcı</th><th className="text-left p-3">E-posta</th><th className="text-left p-3">Rol</th><th></th></tr>
          </thead>
          <tbody>
            {members.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Henüz üye yok. İlk üyeyi ekleyin.</td></tr>}
            {members.map((m) => (
              <tr key={m.id} className="border-t border-border" data-testid={`team-row-${m.id}`}>
                <td className="p-3 font-medium">{m.name || "-"}</td>
                <td className="p-3 text-muted-foreground">{m.email}</td>
                <td className="p-3">
                  <select value={m.role} onChange={(e) => changeRole(m.id, e.target.value)} className="h-8 px-2 border border-border rounded text-xs" data-testid={`role-${m.id}`} disabled={m.role === "owner"}>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td className="p-3 text-right">
                  {m.role !== "owner" && <Button size="sm" variant="ghost" onClick={() => remove(m.id)} data-testid={`del-team-${m.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>}
                  {m.role === "owner" && <span className="text-[10px] text-muted-foreground flex items-center justify-end gap-1"><Shield className="h-3 w-3" /> Sabit</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Yeni Ekip Üyesi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>E-posta</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-email" /></div>
            <div><Label>Ad Soyad (ops)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="member-name" /></div>
            <div><Label>Rol</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 px-3 border border-border rounded" data-testid="member-role">
                {Object.entries(ROLE_LABELS).filter(([k]) => k !== "owner").map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="text-[10px] text-muted-foreground">Not: Yeni kullanıcıya davet e-postası gönderilecek. Var olan kullanıcılar direkt eklenir.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={add} disabled={!form.email} data-testid="save-member-btn">Ekle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
